// ===============================
// IMPORTS
// ===============================
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Icons
import { BarChart3, Hammer } from "lucide-react";

// API and Utils
import api from "../../../../../utils/api";
import L from "leaflet";
import { createCustomIcon, validateCoordinates, BASE_LAYERS } from "./utils";

// Components
import { Button, useToast } from "../../components/AdminSharedComponents";
import { SearchInput } from "../../components/ui/SearchInput";
import { DropdownFilter } from "../../components/ui/DropdownFilter";
import { SearchableDropdownFilter } from "../../components/ui/SearchableDropdownFilter";
import { ResetButton } from "../../components/ui/ResetButton";
import ExcavationList from "./ExcavationList";
import ExcavationStats from "./StatisticsCards";
import Legend from "./Legend";
import MapControls from "./MapControls";

// ===============================
// INTERNAL COMPONENTS
// ===============================

// Component to handle map interactions
const MapController = ({
  selectedExcavation,
  selectedProjectInfo,
  filteredExcavations,
}) => {
  const map = useMap();

  useEffect(() => {
    if (selectedExcavation && map) {
      const validCoords = validateCoordinates(selectedExcavation.coordinates);

      if (validCoords) {
        // Fly to the excavation location with smooth animation
        map.flyTo([validCoords.lat, validCoords.lng], MAP_ZOOM_EXCAVATION, {
          duration: 1.5,
          easeLinearity: 0.1,
        });
      }
    }
  }, [selectedExcavation, map]);

  // Fly to project location when project filter is selected
  useEffect(() => {
    if (!selectedExcavation && selectedProjectInfo && map) {
      const L = window.L;
      if (!L) return;

      // If there are excavation points in this project, fit bounds to all points
      if (filteredExcavations && filteredExcavations.length > 0) {
        const validPoints = filteredExcavations
          .map((e) => validateCoordinates(e.coordinates))
          .filter(Boolean);

        if (validPoints.length > 0) {
          const bounds = L.latLngBounds(validPoints.map((c) => [c.lat, c.lng]));
          map.flyToBounds(bounds.pad(0.1), {
            duration: 1.5,
            easeLinearity: 0.1,
            maxZoom: 18,
          });
          return;
        }
      }

      // If no excavation points, fly to project coordinates
      if (selectedProjectInfo.coordinates) {
        const validCoords = validateCoordinates(
          selectedProjectInfo.coordinates
        );
        if (validCoords) {
          map.flyTo([validCoords.lat, validCoords.lng], 15, {
            duration: 1.5,
            easeLinearity: 0.1,
          });
        }
      }
    }
  }, [selectedExcavation, selectedProjectInfo, filteredExcavations, map]);

  return null;
};

// ===============================
// CONSTANTS
// ===============================
const DEFAULT_MAP_CENTER = [-2.5489, 118.0149];
const DEFAULT_MAP_ZOOM = 5;
const MAP_ZOOM_EXCAVATION = 20;

// ===============================
// COMPONENT
// ===============================
const ExcavationMap = () => {
  // ===============================
  // HOOKS & STATE
  // ===============================
  const mapInstanceRef = useRef(null);
  const toast = useToast();
  const [searchParams] = useSearchParams();

  // URL Parameters
  const excavationId = searchParams.get("excavationId");
  const projectIdParam = searchParams.get("projectId");

  // State
  const [excavations, setExcavations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExcavation, setSelectedExcavation] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [hoveredButton, setHoveredButton] = useState(null);
  const [selectedBaseLayer, setSelectedBaseLayer] = useState(BASE_LAYERS[0]);
  const [isInitialized, setIsInitialized] = useState(false);

  // ===============================
  // COMPUTED VALUES
  // ===============================

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter excavations using useMemo instead of useEffect
  const filteredExcavations = useMemo(() => {
    // Ensure excavations is an array before filtering
    if (!Array.isArray(excavations)) {
      return [];
    }

    let filtered = excavations;

    if (debouncedSearchTerm) {
      filtered = filtered.filter(
        (e) =>
          e.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          e.description
            ?.toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()) ||
          e.contractor
            ?.toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()) ||
          e.Project?.name
            ?.toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((e) => {
        const normalizedStatus = e.status?.toLowerCase();
        const normalizedFilter = statusFilter.toLowerCase();
        return (
          normalizedStatus === normalizedFilter || e.status === statusFilter
        );
      });
    }

    if (projectFilter !== "all") {
      filtered = filtered.filter((e) => e.Project?.name === projectFilter);
    }

    return filtered;
  }, [excavations, debouncedSearchTerm, statusFilter, projectFilter]);

  const statusOptions = useMemo(
    () => [
      { value: "all", label: "All Status" },
      { value: "active", label: "Active" },
      { value: "planned", label: "Planned" },
      { value: "completed", label: "Completed" },
      { value: "suspended", label: "Suspended" },
    ],
    []
  );

  const projectOptions = useMemo(() => {
    // Ensure projects is an array
    if (!Array.isArray(projects) || projects.length === 0)
      return [{ value: "all", label: "All Projects", count: 0 }];

    // Count excavations per project ID
    const projectCounts = (excavations || []).reduce((acc, e) => {
      const projectId = e.project_id;
      if (projectId) {
        acc[projectId] = (acc[projectId] || 0) + 1;
      }
      return acc;
    }, {});

    // Sort projects alphabetically
    const sortedProjects = [...projects].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    return [
      {
        value: "all",
        label: "All Projects",
        count: excavations?.length || 0,
      },
      ...sortedProjects.map((p) => ({
        value: p.name,
        label: p.name,
        count: projectCounts[p.id] || 0,
        projectId: p.id, // Store project ID for navigation
      })),
    ];
  }, [projects, excavations]);

  const statusCounts = useMemo(() => {
    // Ensure excavations is an array before using array methods
    if (!Array.isArray(excavations)) {
      return {
        total: 0,
        active: 0,
        planned: 0,
        completed: 0,
        suspended: 0,
      };
    }

    return {
      total: excavations.length,
      active: excavations.filter(
        (e) => e.status === "active" || e.status === "Active"
      ).length,
      planned: excavations.filter(
        (e) => e.status === "planned" || e.status === "Planned"
      ).length,
      completed: excavations.filter(
        (e) => e.status === "completed" || e.status === "Completed"
      ).length,
      suspended: excavations.filter(
        (e) => e.status === "suspended" || e.status === "Suspended"
      ).length,
    };
  }, [excavations]);

  // ===============================
  // EFFECTS
  // ===============================

  // Fetch projects and excavations
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        // Fetch both projects and excavations in parallel
        const [projectsRes, excavationsRes] = await Promise.all([
          api.get("/api/projects", { signal: controller.signal }),
          api.get("/api/excavation-points", { signal: controller.signal }),
        ]);

        if (isMounted) {
          // Handle projects response
          const projectsData = projectsRes.data.data || projectsRes.data || [];
          setProjects(projectsData);

          // Handle excavations response
          const excavationsData =
            excavationsRes.data.data || excavationsRes.data || [];
          setExcavations(excavationsData);
        }
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Error fetching data:", error);
          toast.error("Failed to load data");
          // Set empty arrays as fallback
          if (isMounted) {
            setProjects([]);
            setExcavations([]);
          }
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===============================
  // UTILITY FUNCTIONS
  // ===============================

  const selectExcavation = useCallback((excavation) => {
    setSelectedExcavation(excavation);
    // The MapController component will handle the fly to animation
  }, []);

  // Auto-select excavation from URL parameter
  useEffect(() => {
    if (
      Array.isArray(excavations) &&
      excavations.length > 0 &&
      excavationId &&
      !isInitialized
    ) {
      const target = excavations.find((e) => String(e.id) === excavationId);
      if (target) {
        selectExcavation(target);
        setIsInitialized(true);
      }
    }
  }, [excavations, excavationId, isInitialized, selectExcavation]);

  // Auto-select project filter from URL parameter (only if no excavationId)
  useEffect(() => {
    if (
      Array.isArray(projects) &&
      projects.length > 0 &&
      projectIdParam &&
      !excavationId && // Only apply project filter if not selecting specific excavation
      !isInitialized &&
      projectFilter === "all"
    ) {
      const targetProject = projects.find(
        (p) => String(p.id) === projectIdParam
      );
      if (targetProject) {
        setProjectFilter(targetProject.name);
        setIsInitialized(true);
      }
    }
  }, [projects, projectIdParam, excavationId, isInitialized, projectFilter]);

  // Get selected project info for empty state and map focus
  const selectedProjectInfo = useMemo(() => {
    if (projectFilter === "all") return null;

    const projectOption = projectOptions.find(
      (opt) => opt.value === projectFilter
    );

    if (!projectOption) return null;

    // Find the full project data to get coordinates
    const fullProject = projects.find((p) => p.id === projectOption.projectId);

    return {
      name: projectOption.label,
      projectId: projectOption.projectId,
      count: projectOption.count,
      coordinates: fullProject?.coordinates || null,
    };
  }, [projectFilter, projectOptions, projects]);

  const handleProjectFilterChange = useCallback(
    (newProjectFilter) => {
      setProjectFilter(newProjectFilter);

      // Clear URL parameters when user manually changes project filter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("projectId");
      newSearchParams.delete("excavationId");
      const queryString = newSearchParams.toString();
      const newUrl = queryString
        ? `${window.location.pathname}?${queryString}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    },
    [searchParams]
  );

  const resetFilters = useCallback(() => {
    setSearchTerm("");
    setStatusFilter("all");
    setProjectFilter("all");
    setSelectedExcavation(null);
    setIsInitialized(false);

    // Clear URL parameters when resetting filters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("projectId");
    newSearchParams.delete("excavationId");
    const queryString = newSearchParams.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
  }, [searchParams]);

  const resetMapView = useCallback(() => {
    mapInstanceRef.current?.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);
  }, []);

  const handleLayerChange = useCallback((layerName) => {
    const layer = BASE_LAYERS.find((l) => l.name === layerName);
    if (layer) {
      setSelectedBaseLayer(layer);
    }
  }, []);

  // ===============================
  // RENDER
  // ===============================

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-pulse text-primary">
          Loading excavation map...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary-dark flex items-center gap-2">
              <Hammer className="h-6 w-6" />
              Excavation Points Map
            </h1>
            <p className="text-gray-600 mt-1">
              View all excavation points and their current status
            </p>
          </div>
          <ExcavationStats statusCounts={statusCounts} />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-120px)]">
        {/* Sidebar */}
        <div
          className={`bg-white border-r border-gray-200 transition-all duration-300 ${
            showSidebar ? "w-full lg:w-96" : "w-0 overflow-hidden"
          }`}
        >
          <div className="h-full flex flex-col">
            <div className="p-5 border-b border-gray-200">
              <div className="space-y-4">
                {/* Search */}
                <SearchInput
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search excavations, projects..."
                />

                {/* Row 1 → Project filter */}
                <div className="grid grid-cols-1">
                  <SearchableDropdownFilter
                    options={projectOptions}
                    value={projectFilter}
                    onChange={handleProjectFilterChange}
                    placeholder="Select Project"
                    showCount={true}
                  />
                </div>

                {/* Row 2 → Status + Reset */}
                <div className="grid grid-cols-2 gap-3">
                  <DropdownFilter
                    options={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    className="w-full"
                  />
                  <ResetButton
                    onClick={resetFilters}
                    disabled={
                      !searchTerm &&
                      statusFilter === "all" &&
                      projectFilter === "all"
                    }
                    className={`w-full ${
                      !searchTerm &&
                      statusFilter === "all" &&
                      projectFilter === "all"
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  />
                </div>
              </div>
            </div>

            <ExcavationList
              excavations={filteredExcavations}
              selectedExcavation={selectedExcavation}
              onSelect={selectExcavation}
              onBack={() => setSelectedExcavation(null)}
              selectedProjectFilter={selectedProjectInfo}
            />
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 relative h-[calc(100vh-120px)]">
          {/* Map Controls */}
          <MapControls
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            resetMapView={resetMapView}
            hoveredButton={hoveredButton}
            setHoveredButton={setHoveredButton}
            selectedBaseLayer={selectedBaseLayer}
            onLayerChange={handleLayerChange}
          />

          {/* Legend */}
          <Legend />

          <MapContainer
            center={DEFAULT_MAP_CENTER}
            zoom={DEFAULT_MAP_ZOOM}
            style={{ height: "100%", width: "100%" }}
            whenCreated={(map) => (mapInstanceRef.current = map)}
          >
            <MapController
              selectedExcavation={selectedExcavation}
              selectedProjectInfo={selectedProjectInfo}
              filteredExcavations={filteredExcavations}
            />
            <TileLayer
              url={selectedBaseLayer.url}
              attribution={selectedBaseLayer.attribution}
            />

            {/* Project Location Marker - Only show when specific project is selected */}
            {selectedProjectInfo &&
              selectedProjectInfo.coordinates &&
              filteredExcavations.length === 0 &&
              (() => {
                const coords = validateCoordinates(
                  selectedProjectInfo.coordinates
                );
                if (!coords) return null;

                // Create custom project icon (simple circle, same as ProjectMap)
                const projectIcon = L.divIcon({
                  html: `<div style="
                  width: 30px;
                  height: 30px;
                  background-color: #3b82f6;
                  border: 3px solid #ffffff;
                  border-radius: 50%;
                  box-shadow: 0 2px 12px rgba(0,0,0,0.3);
                  position: relative;
                ">
                  <div style="
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 12px;
                    height: 12px;
                    background-color: #ffffff;
                    border-radius: 50%;
                  "></div>
                </div>`,
                  className: "custom-project-marker",
                  iconSize: [26, 26],
                  iconAnchor: [13, 13],
                  popupAnchor: [0, -13],
                });

                return (
                  <Marker
                    key={`project-${selectedProjectInfo.projectId}`}
                    position={[coords.lat, coords.lng]}
                    icon={projectIcon}
                  >
                    <Popup
                      className="custom-popup"
                      closeButton={false}
                      autoPan={true}
                      keepInView={true}
                    >
                      <div className="min-w-52 sm:min-w-64">
                        <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                          📍 {selectedProjectInfo.name}
                        </h3>
                        <div className="space-y-2 text-xs sm:text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Project Location
                            </span>
                          </div>
                          <div className="pt-2 border-t text-gray-500 text-xs">
                            Lat: {coords.lat.toFixed(5)}, Long:{" "}
                            {coords.lng.toFixed(5)}
                          </div>
                          <div className="pt-2 border-t">
                            <p className="text-xs text-orange-600 font-medium">
                              Belum ada excavation points di project ini
                            </p>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })()}

            {/* Excavation Points Markers */}
            {Array.isArray(filteredExcavations) &&
              filteredExcavations.map((excavation) => {
                const coords = validateCoordinates(excavation.coordinates);
                if (!coords) return null;

                return (
                  <Marker
                    key={excavation.id}
                    position={[coords.lat, coords.lng]}
                    icon={createCustomIcon(excavation.status)}
                    eventHandlers={{
                      click: () => {
                        selectExcavation(excavation);
                        setShowSidebar(true);
                      },
                    }}
                  >
                    <Popup
                      className="custom-popup"
                      closeButton={false}
                      autoPan={true}
                      keepInView={true}
                    >
                      <div className="min-w-52 sm:min-w-64">
                        <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                          {excavation.name || "Unnamed Point"}
                        </h3>
                        <div className="space-y-2 text-xs sm:text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <span className="font-medium">
                              {excavation.status}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Depth:</span>
                            <span className="font-medium">
                              {excavation.depth
                                ? `${excavation.depth}m`
                                : "N/A"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Project:</span>
                            <span className="font-medium">
                              {excavation.Project?.name || "No project"}
                            </span>
                          </div>
                          <div className="pt-2 border-t text-gray-500 text-xs">
                            Lat: {coords.lat.toFixed(5)}, Long:{" "}
                            {coords.lng.toFixed(5)}
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
          </MapContainer>
        </div>
      </div>

      {/* Mobile Toggle */}
      <div className="lg:hidden fixed bottom-4 right-4 z-[100]">
        <Button
          onClick={() => setShowSidebar(!showSidebar)}
          className="shadow-lg"
        >
          <BarChart3 className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default ExcavationMap;
